import React from 'react';
import { cn } from '@/lib/utils';
import { Droplet, Fan, Snowflake, Flame, Zap } from 'lucide-react';
import { Mode } from '@/types';

interface ModeBadgeProps {
  mode: Mode | null;
  className?: string;
}

export const modeConfig = {
  cool: { icon: Snowflake, label: 'Cool', color: 'text-primary' }, // Blue for cool
  fan: { icon: Fan, label: 'Fan', color: 'text-dark-green' }, // Green for fan
  dry: { icon: Droplet, label: 'Dry', color: 'text-mid-orange' }, // Orange for dry
  heat: { icon: Flame, label: 'Heat', color: 'text-danger' }, // Red for heat
  'auto cool': { icon: Snowflake, label: 'Auto Cool', color: 'text-primary' },
  'auto heat': { icon: Flame, label: 'Auto Heat', color: 'text-danger' },
} as const;

export function ModeBadge({ mode, className }: ModeBadgeProps) {
  if (!mode) return <span className="text-dark-grey">-</span>;

  // Ensure mode is lowercase to handle capitalized enum values from API
  const normalizedMode = typeof mode === 'string' ? mode.toLowerCase() as Mode : mode;

  // Try to find the config for this mode
  const config = modeConfig[normalizedMode as keyof typeof modeConfig];

  // If no config found, use a generic display with the mode name
  if (!config) {
    return (
      <div className={cn("flex items-center justify-center gap-1", className)}>
        <span className="w-5 h-5 flex items-center justify-center">
          <Zap className="w-4 h-4 text-dark-grey" />
        </span>
        <span className="paragraph-11 text-dark-grey capitalize font-inter" style={{ fontFamily: 'Inter, sans-serif !important' }}>
          {normalizedMode}
        </span>
      </div>
    );
  }

  return (
    <div className={cn("flex items-center justify-center gap-1", className)}>
      <span className="w-5 h-5 flex items-center justify-center">
        <config.icon className={cn("w-4 h-4", config.color)} />
      </span>
      <span className={cn("paragraph-11 capitalize font-inter", config.color)} style={{ fontFamily: 'Inter, sans-serif !important' }}>{config.label}</span>
    </div>
  );
}